/**
 * Account balances for voucher entry.
 *
 * One request when the screen opens and one after each save, then every
 * lookup is in memory: the picker shows balances while the user types
 * without calling the server (PROJECT.md decision 7).
 *
 * The server decides what a user may see (a clerk gets customers,
 * suppliers, cash and bank only). An account missing from the map is
 * either zero or not shown to this user; the UI shows nothing for it
 * rather than guessing "0.00", which would be a claim we cannot make.
 */
import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchLedgerBalances } from '../api/endpoints'
import { P } from '../api/types'
import { useAuth } from './AuthContext'
import { useLedgers } from './LedgerCache'
import { signed } from '../vouchers/money'

export const BALANCES_KEY = ['ledger-balances'] as const

export interface BalanceLookup {
  /** Signed balance (Dr +, Cr -), or undefined when not known to this user. */
  of: (code: string) => string | undefined
  /** True once the request has answered (even with nothing). */
  ready: boolean
}

export function useLedgerBalances(): BalanceLookup {
  const { can, me } = useAuth()
  const { byCode } = useLedgers()
  const allowed = can(P.reportOperational)
  const financial = can(P.reportFinancial)
  const q = useQuery({
    queryKey: BALANCES_KEY,
    queryFn: fetchLedgerBalances,
    enabled: allowed,
    staleTime: 30_000,
  })

  const map = useMemo(() => {
    const m = new Map<string, string>()
    for (const b of q.data?.balances ?? []) m.set(b.code, signed(b.balance, b.side))
    return m
  }, [q.data])

  /*
   * Absent from the answer means zero -- but only for an account this user
   * may see. Mirrors the server's rule so a hidden balance never shows as
   * a confident 0.00.
   */
  const visible = useCallback((code: string) => {
    if (financial) return true
    const led = byCode(code)
    if (!led) return false
    const keys = led.group_keys ?? [led.group_key]
    const parties = me?.allowed_parties ?? []
    const tills = me?.allowed_cash_bank ?? []
    if (keys.some((k) => k === 'DEBTORS' || k === 'CREDIT')) {
      return !parties.length || parties.includes(code)
    }
    if (keys.some((k) => k === 'CASH' || k === 'BANK')) {
      return !tills.length || tills.includes(code)
    }
    return false
  }, [financial, byCode, me])

  const of = useCallback((code: string) => {
    if (!q.data || !code) return undefined
    return map.get(code) ?? (visible(code) ? '0.00' : undefined)
  }, [map, q.data, visible])

  return { of, ready: !!q.data }
}
